import { Page } from 'components/Page/Page';
import { useCallback, useEffect, useRef, useState } from 'react';
import i18n from 'i18n';
import {
  SiteAnnouncementListColumn,
  SiteAnnouncementListModel,
  SIZE_PER_PAGE,
  useSiteAnnouncementListModel
} from './SiteAnnouncementListModel';
import { LoadingIndicator } from 'components/common/LoadingIndicator';
import { StickableTanstackTable } from 'components/common/Table/StickableTanstackTable';
import { Link, useHistory, useLocation } from 'react-router-dom';
import {
  SiteAnnouncement,
  SiteAnnouncementStatus
} from 'core/siteAnnouncement/SiteAnnouncement';
import IconWithTooltip from 'components/IconWithTooltip/IconWithTooltip';
import { faPencilAlt, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { Modal } from 'components/common/Modal/Modal';
import { SiteAnnouncementDetailModal } from './components/SiteAnnouncementDetailModal';
import styles from './siteAnnouncementList.module.scss';

const REFRESH_READ_STATUS_TIMEOUT = 5000; // 5 seconds

export const SiteAnnouncementList: React.FC = () => {
  const location = useLocation<
    { autoOpenAnnouncementId?: number } | undefined
  >();
  const history = useHistory();
  const autoOpenAnnouncementId = location.state?.autoOpenAnnouncementId;

  const {
    loading,
    isAdmin,
    searchString,
    filteredAnnouncements,
    selectedAnnouncements,
    columns,
    batchOperations,
    onTableChange,
    onHandleSearch,
    onHandleSelect,
    onHandleSelectPage,
    onClearSelect,
    markAnnouncementsAsRead,
    getAnnouncementRowClasses,
    detailAnnouncement,
    onHideDetail,
    showDeleteConfirmModal,
    onShowDeleteConfirmModal,
    onHideDeleteConfirmModal,
    onConfirmDelete
  }: SiteAnnouncementListModel = useSiteAnnouncementListModel(
    autoOpenAnnouncementId
  );

  const readTimeoutRef = useRef<NodeJS.Timeout>();
  const [, refreshReadStatus] = useState(false);

  useEffect(() => {
    const handler = () => refreshReadStatus((prev) => !prev);
    window.addEventListener('announcements-read', handler);
    return () => window.removeEventListener('announcements-read', handler);
  }, []);

  useEffect(() => {
    refreshReadStatus(false);
    let readTimeoutId = readTimeoutRef.current;
    readTimeoutId && clearTimeout(readTimeoutId);

    readTimeoutId = setTimeout(() => {
      markAnnouncementsAsRead();
      refreshReadStatus(true);
      history.replace({ pathname: location.pathname, state: undefined });
    }, REFRESH_READ_STATUS_TIMEOUT);

    readTimeoutRef.current = readTimeoutId;
    return () => {
      readTimeoutId && clearTimeout(readTimeoutId);
    };
  }, [markAnnouncementsAsRead, history, location.pathname]);

  const renderListOperations = useCallback(
    () => [
      <Link
        key='new-btn'
        to={'/site-announcements/new'}
        className={'btn btn-tertiary btn-sm'}
      >
        {i18n.t('siteAnnouncementList.buttons.create')}
      </Link>
    ],
    []
  );

  const handleShowDeleteConfirmModal = useCallback(
    (announcementId: number) => (event?: React.MouseEvent) => {
      event && event.stopPropagation();
      onShowDeleteConfirmModal([announcementId]);
    },
    [onShowDeleteConfirmModal]
  );

  const canDeleteAnnouncement = useCallback(
    (announcement: SiteAnnouncement) => {
      // Only allow deletion of unpublished announcements
      return announcement.status === SiteAnnouncementStatus.UNPUBLISHED;
    },
    []
  );

  const renderRowBtns = useCallback(
    (announcement: SiteAnnouncement) => [
      <IconWithTooltip
        key={`edit-btn-${announcement.id}`}
        icon={faPencilAlt}
        tooltipProps={{
          id: `edit-site-announcements-${announcement.id}`,
          link: `/site-announcements/${announcement.id}/edit`,
          tooltip: i18n.t('siteAnnouncementList.hints.edit')
        }}
      />,
      <IconWithTooltip
        key={`delete-btn-${announcement.id}`}
        icon={faTrashAlt}
        disabled={!canDeleteAnnouncement(announcement)}
        onClick={handleShowDeleteConfirmModal(announcement.id)}
        tooltipProps={{
          id: `delete-site-announcements-${announcement.id}`,
          tooltip: canDeleteAnnouncement(announcement)
            ? i18n.t('common.buttons.delete')
            : i18n.t('siteAnnouncementList.hints.cannotDelete')
        }}
      />
    ],
    [handleShowDeleteConfirmModal, canDeleteAnnouncement]
  );

  // Apply useCallback & instant execution instead of useMemo to avoid unnecessary re-renders and performance issues
  const tablePropsOfRole = useCallback(
    () =>
      isAdmin
        ? {
            selectedRows: selectedAnnouncements,
            batchOperaionConfig: batchOperations,
            noDataIndication: i18n.t(
              'siteAnnouncementList.placeholders.adminNoData'
            ),
            renderRowBtns: renderRowBtns,
            onSelect: onHandleSelect,
            onSelectPage: onHandleSelectPage,
            renderListOperations: renderListOperations
          }
        : {
            noDataIndication: i18n.t('siteAnnouncementList.placeholders.noData')
          },
    [
      isAdmin,
      selectedAnnouncements,
      batchOperations,
      renderRowBtns,
      onHandleSelect,
      onHandleSelectPage,
      renderListOperations
    ]
  )();

  return (
    <Page title={i18n.t('siteAnnouncementList.title')}>
      {loading && <LoadingIndicator />}
      <div className={styles.siteAnnouncementList}>
        <StickableTanstackTable
          customPadding={false}
          keyField={SiteAnnouncementListColumn.ID}
          data={filteredAnnouncements}
          columns={columns}
          rowClasses={getAnnouncementRowClasses}
          defaultSearchString={searchString}
          searchbarPlaceholder={i18n.t(
            isAdmin
              ? 'siteAnnouncementList.placeholders.adminSearchbar'
              : 'siteAnnouncementList.placeholders.searchbar'
          )}
          onHandleSearch={onHandleSearch}
          clearSelectedRows={onClearSelect}
          onTableChange={onTableChange}
          pagination={{
            sizePerPage: SIZE_PER_PAGE
          }}
          {...tablePropsOfRole}
        />
      </div>
      {showDeleteConfirmModal && (
        <Modal
          title={i18n.t('siteAnnouncementList.labels.deleteModalTitle')}
          secondaryButton={{
            title: i18n.t('common.buttons.cancel'),
            callback: onHideDeleteConfirmModal
          }}
          primaryButton={{
            title: i18n.t('common.buttons.delete'),
            callback: onConfirmDelete
          }}
          dismiss={onHideDeleteConfirmModal}
        >
          {i18n.t('siteAnnouncementList.labels.deleteModalContent')}
        </Modal>
      )}
      {detailAnnouncement && (
        <SiteAnnouncementDetailModal
          announcement={detailAnnouncement}
          onClose={onHideDetail}
        />
      )}
    </Page>
  );
};
